import clsx from 'clsx';
import { Article } from 'interfaces/homepage';
import React from 'react';

import Byline from '@components/Byline';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ClickTarget, ContentType, TrackingLocation, click } from '@tracking';
import { UtmParams } from '@utils/queryStringHelper';
import { articlePathWithParams } from '@utils/queryStringHelper';

import { SectionTitle } from './SectionTitle';

interface RecommendedArticlesProps {
  className?: string;
  recommendedArticles: Article[];
  skipSectionTitle?: boolean;
  trackingLocation?: TrackingLocation;
  offerCode?: string;
  title?: string;
  utmParams?: UtmParams;
}

const RecommendedArticles = ({
  className,
  recommendedArticles,
  skipSectionTitle = false,
  trackingLocation = TrackingLocation.RecommendedArticles,
  offerCode,
  title = 'Recommended',
  utmParams,
}: RecommendedArticlesProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: trackingLocation,
  });

  const hydrated = useServerSideRendering();

  if (!hydrated) {
    return null;
  }

  return (
    <Track>
      <div className="mb-64">
        {!skipSectionTitle && <SectionTitle title={title} />}
        {recommendedArticles.map((article, index) => (
          <div key={`${article.title}-${index}`} className={clsx('mb-[30px]', className)}>
            <h2
              className="mb-4 mt-0 font-suisse-neue text-body-md text-primary-black"
              data-testid="recommended-article-title"
            >
              <a
                href={articlePathWithParams({
                  slug: article.slug,
                  utm: utmParams,
                  offer: offerCode,
                  newsletterSlug: article.newsletterSlug,
                })}
                onClickCapture={() =>
                  trackEvent(
                    click({
                      contentType: ContentType.Article,
                      contentId: article.id,
                      contentIndex: index,
                      clickTarget: ClickTarget.RecommendedArticle,
                      clickText: article.title,
                      creatorIds: article.authors?.map((author) => author.id),
                    })
                  )
                }
                className="text-balance"
              >
                <div className="hover:text-primary-magenta">{article.title}</div>
              </a>
            </h2>
            <Byline authors={article.authors} timeFromNow newsletterSlug={article.newsletterSlug} />
          </div>
        ))}
      </div>
    </Track>
  );
};

export default RecommendedArticles;
